import { CONSENT_DISABLED_POLICY } from '@belong/common';
import { hosted, offline } from 'c15t';
import { isProd } from 'src/utils/is-prod';

/* -------------------------------------------------------------------------- */
/*                              Environment gates                             */
/* -------------------------------------------------------------------------- */

/**
 * Whether the production-only tracking vendors should be registered.
 *
 * Reddit, Retention and Midbound have always been gated on
 * `isProd`, which means the interesting half of consent gating — the actual
 * marketing pixels — is invisible on dev1/2/3 and staging. That makes the
 * gating impossible to verify without a production deploy.
 *
 * `NEXT_PUBLIC_C15T_FORCE_PROD_SCRIPTS=1` registers them off production so the
 * gating can be exercised on a preview deploy. Set it on staging only, and
 * only against test pixel IDs — it sends real events to whatever IDs are
 * configured for that environment.
 */
export const TRACKING_SCRIPTS_ENABLED =
  isProd || process.env.NEXT_PUBLIC_C15T_FORCE_PROD_SCRIPTS === '1';

/**
 * DevTools is gated on the deployment environment rather than `NODE_ENV` so it
 * is available on staging previews, where most of the policy/geo verification
 * actually happens. It stays out of production.
 */
export const CONSENT_DEV_TOOLS_ENABLED = !isProd;

/**
 * Temporary kill switch for the entire consent-gating layer.
 *
 * `NEXT_PUBLIC_C15T_DISABLED=1` forces every visitor onto c15t's own
 * auto-grant path (`model: 'opt-out'` with no stored `consentInfo` ->
 * `calculateAutoGrantedConsents` grants every category on arrival) — with the
 * client pinned to offline mode
 * so nothing depends on the consent backend while it is set. The
 * banner never renders (`ui.mode: 'none'`), and every vendor script keeps
 * registering and firing exactly as `buildConsentScripts` already has them
 * wired — nothing there changes, because it already self-gates on whatever
 * consent state c15t resolves.
 *
 * No `isProd` guard: enabling it anywhere, including Production, already goes
 * through a deliberate Vercel env-var change plus a redeploy, not something
 * that happens by accident. Same posture as `NEXT_PUBLIC_MAINTENANCE_MODE`.
 *
 * Unset (or anything other than `'1'`) restores normal gating, and it takes
 * effect on the very next page load with nothing to clear: the auto-grant
 * branch never persists to the `c15t` storage key (verified against the
 * shipped bundle — `consentInfo` stays `null` throughout, exactly as it must
 * for the auto-grant condition to keep holding), so there is no stored record
 * for the real policy to override. `belong-web` reads the same switch. See
 * "Temporarily disabling all gating" in `docs/development/consent-management.md`.
 */
export const CONSENT_DISABLED = process.env.NEXT_PUBLIC_C15T_DISABLED === '1';

/* -------------------------------------------------------------------------- */
/*                                Client wiring                               */
/* -------------------------------------------------------------------------- */

/** Proxies to Octane's consent service, `src/pages/api/c15t/[[...path]].ts`. */
const C15T_BACKEND_URL = '/api/c15t';

export const c15tMode = CONSENT_DISABLED
  ? offline({ policyRules: [CONSENT_DISABLED_POLICY] })
  : hosted({ backendURL: C15T_BACKEND_URL });

/**
 * Dev-only geolocation override.
 *
 * Policy resolution is geo-driven, which makes it awkward to verify: the
 * interesting cases (US-CA opt-in vs. US opt-out vs. EU opt-in vs. Quebec)
 * are four different locations. Rather than require a VPN, these let a preview
 * deploy be pointed at a specific location.
 *
 * Production ignores them outright — a hardcoded override there would apply the
 * wrong jurisdiction's rules to every real visitor. DevTools also exposes the
 * same controls interactively; this exists so the checks can be scripted.
 */
export const c15tOverrides =
  !isProd && process.env.NEXT_PUBLIC_C15T_OVERRIDE_COUNTRY
    ? {
        country: process.env.NEXT_PUBLIC_C15T_OVERRIDE_COUNTRY,
        ...(process.env.NEXT_PUBLIC_C15T_OVERRIDE_REGION
          ? { region: process.env.NEXT_PUBLIC_C15T_OVERRIDE_REGION }
          : {}),
      }
    : undefined;

/* -------------------------------------------------------------------------- */
/*                                  Telemetry                                 */
/* -------------------------------------------------------------------------- */

/**
 * Prefixes every consent log line so the console and the Datadog log explorer
 * share one greppable token.
 */
export const CONSENT_LOG_PREFIX = '[consent]';

/**
 * Bounds the pixel queue so a long session that never grants consent cannot
 * grow it without limit. Lead events are rare; overflow means something is
 * wrong, so it is logged rather than silently dropped.
 */
export const MAX_QUEUED_EVENTS = 50;

/* -------------------------------------------------------------------------- */
/*                             Script registration                            */
/* -------------------------------------------------------------------------- */

/**
 * Routes where Retention.com is allowed to run.
 *
 * Moved here from `src/pages/_app.tsx` so the consent layer owns it alongside
 * the script definition. The narrow list is intentional and is not a
 * performance optimisation: Retention.com resolves anonymous visitors to email
 * addresses, so restricting where it runs restricts the exposure. Widening this
 * set is a privacy decision, not a routing one.
 */
export const RETENTION_PATHNAMES = new Set([
  '/',
  '/homeowners',
  '/homeowners/[cityOrRegionSlug]',
  '/homeowners/[cityOrRegionSlug]/[neighborhoodSlug]',
  '/blog',
  '/blog/[uid]',
  '/blog/author/[authorSlug]',
  '/blog/categories/[category]',
]);

/**
 * Per-vendor ids. Each one is optional: an unset id means that vendor is not
 * registered at all, which is how non-production environments run with a
 * subset of the tracking stack.
 */
export const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
export const GTM_VIEW = process.env.NEXT_PUBLIC_GTM_VIEW;
export const GA4_ID = process.env.NEXT_PUBLIC_GA4_ID;
export const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_API_KEY;
export const REDDIT_PIXEL_ID = process.env.NEXT_PUBLIC_REDDIT_PIXEL_ID;
export const RETENTION_KEY = process.env.NEXT_PUBLIC_RETENTION_KEY;
export const MIDBOUND_ID = process.env.NEXT_PUBLIC_MIDBOUND_ID;
export const CUSTOMERIO_WRITE_KEY =
  process.env.NEXT_PUBLIC_CUSTOMERIO_ANALYTICS_WRITE_KEY;
